import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ModelCard, CarDemoModal } from "@/components/FleetCard";

const FILTERS = ["Tutte le categorie", "CITY", "COMPACT", "SUV / CROSSOVER", "SPORT", "PREMIUM", "SCOOTER"];

export const Fleet = ({ categories, days, onChoose }) => {
  const [filter, setFilter] = useState("Tutte le categorie");
  const [demo, setDemo] = useState(null);

  const catByModel = useMemo(() => {
    const m = {};
    categories.forEach((c) => c.models.forEach((g) => (m[`${g.brand} ${g.model}`] = c)));
    return m;
  }, [categories]);

  const visibleModels = useMemo(() => {
    const cats = filter === "Tutte le categorie" ? categories : categories.filter((c) => c.category === filter);
    return cats.flatMap((c) => c.models.map((g) => ({ ...g, category: c })));
  }, [categories, filter]);

  return (
    <section id="flotta" className="relative px-3 md:px-6 pt-8 md:pt-12 pb-4 max-w-6xl mx-auto" data-testid="fleet-section">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          className="text-2xl md:text-3xl font-extrabold tracking-tight text-fr-ink"
          data-testid="fleet-title"
        >
          La nostra flotta a Napoli
        </motion.h2>
        <button
          onClick={() => setFilter("Tutte le categorie")}
          className="ml-auto text-xs font-semibold text-gray-500 hover:text-fr-navy underline underline-offset-2 transition-colors"
          data-testid="fleet-see-all"
        >
          Vedi tutti
        </button>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="fleet-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`relative shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              filter === f ? "text-white" : "text-gray-600 bg-white border border-gray-200 hover:border-fr-navy"
            }`}
            data-testid={`fleet-filter-${f.replace(/[^a-z]/gi, "").toLowerCase() || "all"}`}
          >
            {filter === f && (
              <motion.span layoutId="fleet-pill" className="absolute inset-0 rounded-full bg-fr-ink" transition={{ type: "spring", bounce: 0.18, duration: 0.5 }} />
            )}
            <span className="relative z-10">{f}</span>
          </button>
        ))}
      </div>

      <motion.div layout className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="fleet-grid">
        <AnimatePresence mode="popLayout">
          {visibleModels.map((m, i) => (
            <motion.div
              key={`${m.brand}-${m.model}-${m.category.category}`}
              layout
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.3), ease: [0.16, 1, 0.3, 1] }}
            >
              <ModelCard group={m} category={catByModel[`${m.brand} ${m.model}`]} days={days} onChoose={onChoose} onDemo={setDemo} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <CarDemoModal group={demo} category={demo ? catByModel[`${demo.brand} ${demo.model}`] : null} days={days} onChoose={onChoose} onClose={() => setDemo(null)} />
    </section>
  );
};

export default Fleet;
